import { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '../../components/DataTable/DataTable';
import { useContactMutations, useContacts, useTenantUsers } from '../../hooks';
import type {
  ContactRole,
  IContact,
  IContactInput,
  ITenantSummary,
  ITenantUser,
} from '../../types';
import { initialsOf } from '../Tenants/Tenants';
import styles from './TenantContacts.module.scss';

const ROLES: { id: ContactRole; label: string; hint: string }[] = [
  { id: 'billing', label: 'Billing', hint: 'receives invoices and statements' },
  { id: 'technical', label: 'Technical', hint: 'integration and incident contact' },
  { id: 'executive', label: 'Executive', hint: 'commercial owner, renewals' },
  { id: 'support', label: 'Support', hint: 'day-to-day questions' },
  { id: 'other', label: 'Other', hint: '' },
];

const ROLE_LABEL = Object.fromEntries(ROLES.map(({ id, label }) => [id, label])) as Record<
  ContactRole,
  string
>;

const empty = (): IContactInput => ({
  name: '',
  email: '',
  job_title: null,
  roles: ['billing'],
  phone: null,
  notes: null,
  workos_user_id: null,
});

const fromContact = (contact: IContact): IContactInput => ({
  name: contact.name,
  email: contact.email,
  job_title: contact.job_title,
  roles: contact.roles,
  phone: contact.phone,
  notes: contact.notes,
  workos_user_id: contact.workos_user_id,
});

interface EditorProps {
  initial: IContactInput;
  title: string;
  users: ITenantUser[];
  saving: boolean;
  onClose: () => void;
  onSave: (input: IContactInput) => void;
  onDelete?: () => void;
}

const Editor = ({ initial, title, users, saving, onClose, onSave, onDelete }: EditorProps) => {
  const [draft, setDraft] = useState<IContactInput>(initial);
  useEffect(() => setDraft(initial), [initial]);
  const set = <K extends keyof IContactInput>(key: K, value: IContactInput[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const pickUser = (id: string) => {
    const user = users.find((entry) => entry.id === id);
    if (!user) {
      set('workos_user_id', null);
      return;
    }
    setDraft((current) => ({
      ...current,
      workos_user_id: user.id,
      name: current.name || [user.first_name, user.last_name].filter(Boolean).join(' '),
      email: current.email || user.email,
    }));
  };

  const toggleRole = (role: ContactRole) =>
    set(
      'roles',
      draft.roles.includes(role)
        ? draft.roles.filter((item) => item !== role)
        : [...draft.roles, role],
    );

  return (
    <aside className={styles.panel} aria-label={title}>
      <header className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>{title}</h2>
        <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          onSave({
            ...draft,
            name: draft.name.trim(),
            email: draft.email.trim(),
            job_title: draft.job_title?.trim() || null,
            phone: draft.phone?.trim() || null,
            notes: draft.notes?.trim() || null,
          });
        }}
      >
        {users.length > 0 ? (
          <label className={styles.field}>
            <span>Pick from WorkOS (optional)</span>
            <select
              value={draft.workos_user_id ?? ''}
              onChange={(event) => pickUser(event.target.value)}
            >
              <option value="">Not a platform user</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {[user.first_name, user.last_name].filter(Boolean).join(' ') || user.email} ·{' '}
                  {user.email}
                </option>
              ))}
            </select>
            <small>
              Links this contact to an existing login. Name and email prefill; you can still edit
              them.
            </small>
          </label>
        ) : null}
        <label className={styles.field}>
          <span>Name</span>
          <input
            value={draft.name}
            onChange={(event) => set('name', event.target.value)}
            required
          />
        </label>
        <label className={styles.field}>
          <span>Email</span>
          <input
            type="email"
            value={draft.email}
            onChange={(event) => set('email', event.target.value)}
            required
          />
        </label>
        <div className={styles.row}>
          <label className={styles.field}>
            <span>Job title</span>
            <input
              value={draft.job_title ?? ''}
              onChange={(event) => set('job_title', event.target.value)}
              placeholder="e.g. Finance Manager"
            />
          </label>
          <label className={styles.field}>
            <span>Phone</span>
            <input
              value={draft.phone ?? ''}
              onChange={(event) => set('phone', event.target.value)}
            />
          </label>
        </div>
        <fieldset className={styles.fieldset}>
          <legend>Contact for</legend>
          <div className={styles.roleGrid}>
            {ROLES.map((role) => (
              <label
                key={role.id}
                className={styles.roleOption}
                data-on={draft.roles.includes(role.id)}
              >
                <input
                  type="checkbox"
                  checked={draft.roles.includes(role.id)}
                  onChange={() => toggleRole(role.id)}
                />
                <span className={styles.roleLabel}>{role.label}</span>
                {role.hint ? <span className={styles.roleHint}>{role.hint}</span> : null}
              </label>
            ))}
          </div>
        </fieldset>
        <label className={styles.field}>
          <span>Notes</span>
          <textarea
            rows={3}
            value={draft.notes ?? ''}
            onChange={(event) => set('notes', event.target.value)}
            placeholder="PO numbers, invoice format, anything the next person needs to know"
          />
        </label>
        <div className={styles.actions}>
          {onDelete ? (
            <button type="button" className={styles.danger} onClick={onDelete} disabled={saving}>
              Remove
            </button>
          ) : null}
          <span className={styles.spacer} />
          <button type="button" className={styles.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={styles.primary}
            disabled={
              saving || !draft.name.trim() || !draft.email.trim() || draft.roles.length === 0
            }
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </aside>
  );
};

export const TenantContacts = ({ tenant }: { tenant: ITenantSummary }) => {
  const { data: contacts = [], isLoading, error } = useContacts(tenant.name);
  const { data: usersData } = useTenantUsers(tenant.name);
  const { create, update, remove } = useContactMutations(tenant.name);
  const [editing, setEditing] = useState<'new' | string | null>(null);
  const users = usersData?.users ?? [];
  const current =
    editing && editing !== 'new'
      ? contacts.find((contact) => contact.id === editing) ?? null
      : null;
  const saving = create.isLoading || update.isLoading || remove.isLoading;

  const columns = useMemo<ColumnDef<IContact, any>[]>(
    () => [
      {
        id: 'person',
        header: 'Person',
        accessorFn: (row) => `${row.name} ${row.email} ${row.job_title ?? ''}`,
        size: 300,
        cell: ({ row }) => (
          <div className={styles.person}>
            <span className={styles.avatar} aria-hidden="true">
              {initialsOf(row.original.name)}
            </span>
            <div className={styles.personText}>
              <span className={styles.personName}>
                {row.original.name}
                {row.original.workos_user_id ? (
                  <span className={styles.linked} title="Also a platform user">
                    WorkOS
                  </span>
                ) : null}
              </span>
              <span className={styles.personEmail}>{row.original.email}</span>
            </div>
          </div>
        ),
      },
      {
        header: 'Job title',
        accessorKey: 'job_title',
        size: 200,
        cell: ({ getValue }) =>
          getValue<string | null>() ?? <span className={styles.muted}>—</span>,
      },
      {
        header: 'Contact for',
        accessorKey: 'roles',
        size: 240,
        enableSorting: false,
        cell: ({ getValue }) => (
          <span className={styles.roles}>
            {getValue<ContactRole[]>().map((role) => (
              <span key={role} className={styles.roleChip} data-role={role}>
                {ROLE_LABEL[role]}
              </span>
            ))}
          </span>
        ),
      },
      {
        header: 'Phone',
        accessorKey: 'phone',
        size: 160,
        cell: ({ getValue }) =>
          getValue<string | null>() ?? <span className={styles.muted}>—</span>,
      },
      {
        header: 'Notes',
        accessorKey: 'notes',
        enableSorting: false,
        cell: ({ getValue }) => getValue<string | null>() ?? '',
      },
    ],
    [],
  );

  const billing = contacts.filter((contact) => contact.roles.includes('billing'));

  if (isLoading) return <p className={styles.muted}>Loading contacts…</p>;
  if (error)
    return (
      <p className={styles.error}>
        Could not load: {(error as { message?: string }).message ?? 'unknown error'}
      </p>
    );

  return (
    <div className={editing ? styles.split : undefined}>
      <div className={styles.main}>
        <div className={styles.topRow}>
          <p className={styles.muted}>
            {contacts.length} contact{contacts.length === 1 ? '' : 's'}.{' '}
            {billing.length === 0 ? (
              <span className={styles.warn}>No billing contact: invoices have nowhere to go.</span>
            ) : (
              `Invoices go to ${billing.map((contact) => contact.email).join(', ')}.`
            )}
          </p>
          <button type="button" className={styles.primary} onClick={() => setEditing('new')}>
            + Add contact
          </button>
        </div>
        <DataTable
          columns={columns}
          data={contacts}
          storageKey="nexus-ui.tenant.contacts"
          onRowClick={(row) => setEditing(row.id)}
          emptyMessage="No contacts yet. Add the people you send bills to."
        />
      </div>
      {editing === 'new' ? (
        <Editor
          initial={empty()}
          title="Add contact"
          users={users}
          saving={saving}
          onClose={() => setEditing(null)}
          onSave={(input) => create.mutate(input, { onSuccess: () => setEditing(null) })}
        />
      ) : null}
      {current ? (
        <Editor
          initial={fromContact(current)}
          title={current.name}
          users={users}
          saving={saving}
          onClose={() => setEditing(null)}
          onSave={(input) =>
            update.mutate({ id: current.id, input }, { onSuccess: () => setEditing(null) })
          }
          onDelete={() => remove.mutate(current.id, { onSuccess: () => setEditing(null) })}
        />
      ) : null}
    </div>
  );
};
